import { useEffect, useState, useMemo, useCallback } from 'react';
import { useTheme } from '../../context/ThemeContext';
import { adminProducts, adminStockNotifications } from '../../api/admin';
import type { StockNotificationAdmin } from '../../api/admin';
import {
  Package, Search, Filter, ToggleLeft, ToggleRight,
  ChevronUp, ChevronDown, Edit3, Trash2, X,
  Plus, Save, Image, FileText, Tag, DollarSign, Bell, Upload, Loader2,
} from 'lucide-react';

interface ProductRow {
  id: string;
  name: string;
  brand: string;
  flavor: string;
  strength: number;
  strength_unit: string;
  price: number;
  original_price: number | null;
  description: string;
  long_description: string | null;
  image: string | null;
  in_stock: boolean;
  featured: boolean;
  best_seller: boolean;
  is_new: boolean;
  rating: number;
  review_count: number;
  stock_quantity: number;
  max_order_qty: number;
  max_order_qty_enabled: boolean;
  pouches_per_can: number;
  format: string;
  category: string;
  color_accent: string;
}

type SortKey = 'name' | 'brand' | 'price' | 'strength' | 'rating';
type SortDir = 'asc' | 'desc';

const BRANDS = ['ZYN', 'VELO', 'VELO Shift', 'Pablo', 'White Fox', 'CLEW'];
const CATEGORIES = ['Mint', 'Fruity', 'Citrus', 'Berry', 'Tropical', 'Other'];

const emptyProduct: Omit<ProductRow, 'rating' | 'review_count'> = {
  id: '',
  name: '',
  brand: 'ZYN',
  flavor: '',
  strength: 6,
  strength_unit: 'mg',
  price: 1799,
  original_price: null,
  description: '',
  long_description: null,
  image: null,
  in_stock: true,
  featured: false,
  best_seller: false,
  is_new: false,
  stock_quantity: 100,
  max_order_qty: 10,
  max_order_qty_enabled: false,
  pouches_per_can: 20,
  format: 'Slim',
  category: 'Mint',
  color_accent: '#10b981',
};

export default function InventoryPage() {
  const { isDark } = useTheme();
  const [products, setProducts] = useState<ProductRow[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [brandFilter, setBrandFilter] = useState<string>('all');
  const [stockFilter, setStockFilter] = useState<'all' | 'in' | 'out'>('all');
  const [sortKey, setSortKey] = useState<SortKey>('name');
  const [sortDir, setSortDir] = useState<SortDir>('asc');

  // Edit modal state
  const [editProduct, setEditProduct] = useState<ProductRow | null>(null);
  const [isCreating, setIsCreating] = useState(false);
  const [saving, setSaving] = useState(false);
  const [saveError, setSaveError] = useState<string | null>(null);
  const [uploading, setUploading] = useState(false);
  const [imagePreview, setImagePreview] = useState<string | null>(null);
  const [pendingImageFile, setPendingImageFile] = useState<File | null>(null);

  // Dynamic brand/category lists with add-new support
  const [customBrands, setCustomBrands] = useState<string[]>([]);
  const [customCategories, setCustomCategories] = useState<string[]>([]);
  const [addingNewBrand, setAddingNewBrand] = useState(false);
  const [addingNewCategory, setAddingNewCategory] = useState(false);
  const [newBrandName, setNewBrandName] = useState('');
  const [newCategoryName, setNewCategoryName] = useState('');

  // Tab state
  const [activeTab, setActiveTab] = useState<'products' | 'notifications'>('products');

  // Notifications state
  const [notifications, setNotifications] = useState<StockNotificationAdmin[]>([]);
  const [notifLoading, setNotifLoading] = useState(false);

  const loadNotifications = useCallback(() => {
    setNotifLoading(true);
    adminStockNotifications.list()
      .then(setNotifications)
      .catch(console.error)
      .finally(() => setNotifLoading(false));
  }, []);

  useEffect(() => {
    loadProducts();
    loadNotifications();
  }, [loadNotifications]);

  const loadProducts = () => {
    setLoading(true);
    adminProducts.list(1, 200).then(res => {
      setProducts(res.items as unknown as ProductRow[]);
    }).catch(console.error).finally(() => setLoading(false));
  };

  const brands = useMemo(() => {
    const set = new Set([...BRANDS, ...customBrands, ...products.map(p => p.brand)]);
    return Array.from(set).filter(Boolean).sort();
  }, [products, customBrands]);

  const categories = useMemo(() => {
    const set = new Set([...CATEGORIES, ...customCategories, ...products.map(p => p.category)]);
    return Array.from(set).filter(Boolean).sort();
  }, [products, customCategories]);

  const filtered = useMemo(() => {
    let list = [...products];
    if (search) {
      const q = search.toLowerCase();
      list = list.filter(p => p.name.toLowerCase().includes(q) || (p.flavor ?? '').toLowerCase().includes(q));
    }
    if (brandFilter !== 'all') list = list.filter(p => p.brand === brandFilter);
    if (stockFilter === 'in') list = list.filter(p => p.in_stock);
    if (stockFilter === 'out') list = list.filter(p => !p.in_stock);

    list.sort((a, b) => {
      const av = a[sortKey], bv = b[sortKey];
      if (typeof av === 'string') return sortDir === 'asc' ? av.localeCompare(bv as string) : (bv as string).localeCompare(av);
      return sortDir === 'asc' ? (av as number) - (bv as number) : (bv as number) - (av as number);
    });
    return list;
  }, [products, search, brandFilter, stockFilter, sortKey, sortDir]);

  const toggleStock = async (p: ProductRow) => {
    if (!confirm(`Are you sure you want to ${p.in_stock ? 'disable' : 'enable'} "${p.name}"?`)) return;
    try {
      await adminProducts.update(p.id, { in_stock: !p.in_stock });
      setProducts(prev => prev.map(x => x.id === p.id ? { ...x, in_stock: !x.in_stock } : x));
    } catch (err) { console.error(err); }
  };

  const deleteProduct = async (p: ProductRow) => {
    if (!confirm(`Delete "${p.name}"? This cannot be undone.`)) return;
    try {
      await adminProducts.delete(p.id);
      setProducts(prev => prev.filter(x => x.id !== p.id));
    } catch (err) { console.error(err); }
  };

  const handleSort = (key: SortKey) => {
    if (sortKey === key) setSortDir(d => d === 'asc' ? 'desc' : 'asc');
    else { setSortKey(key); setSortDir('asc'); }
  };

  const sortIcon = (k: SortKey) =>
    sortKey === k ? (sortDir === 'asc' ? <ChevronUp size={14} /> : <ChevronDown size={14} />) : null;

  // Open edit modal
  const openEdit = (p: ProductRow) => {
    setEditProduct({ ...p });
    setIsCreating(false);
    setImagePreview(null);
    setSaveError(null);
  };

  // Open create modal
  const openCreate = () => {
    setEditProduct({ ...emptyProduct, rating: 0, review_count: 0 } as ProductRow);
    setIsCreating(true);
    setImagePreview(null);
    setPendingImageFile(null);
    setSaveError(null);
  };

  // Save edited/created product
  const handleSave = async () => {
    if (!editProduct) return;
    setSaving(true);
    setSaveError(null);
    try {
      // Upload pending image first (named by product name + strength)
      let imagePath = editProduct.image;
      if (pendingImageFile) {
        setUploading(true);
        imagePath = await adminProducts.uploadImage(
          pendingImageFile,
          editProduct.brand,
          editProduct.name,
          String(editProduct.strength),
        );
        setUploading(false);
      }

      if (isCreating) {
        const slug = editProduct.name.toLowerCase().replace(/[^a-z0-9]+/g, '-').replace(/^-|-$/g, '');
        const payload = {
          id: slug || `product-${Date.now()}`,
          name: editProduct.name,
          brand: editProduct.brand,
          flavor: editProduct.flavor,
          strength: editProduct.strength,
          strength_unit: editProduct.strength_unit,
          price: editProduct.price,
          original_price: editProduct.original_price,
          description: editProduct.description,
          long_description: editProduct.long_description,
          image: imagePath,
          in_stock: editProduct.in_stock,
          featured: editProduct.featured,
          best_seller: editProduct.best_seller,
          is_new: editProduct.is_new,
          pouches_per_can: editProduct.pouches_per_can,
          format: editProduct.format,
          category: editProduct.category,
          color_accent: editProduct.color_accent,
          stock_quantity: editProduct.stock_quantity,
          max_order_qty: editProduct.max_order_qty,
          max_order_qty_enabled: editProduct.max_order_qty_enabled,
        };
        await adminProducts.create(payload);
      } else {
        const payload = {
          name: editProduct.name,
          brand: editProduct.brand,
          flavor: editProduct.flavor,
          strength: editProduct.strength,
          strength_unit: editProduct.strength_unit,
          price: editProduct.price,
          original_price: editProduct.original_price,
          description: editProduct.description,
          long_description: editProduct.long_description,
          image: imagePath,
          in_stock: editProduct.in_stock,
          featured: editProduct.featured,
          best_seller: editProduct.best_seller,
          is_new: editProduct.is_new,
          pouches_per_can: editProduct.pouches_per_can,
          format: editProduct.format,
          category: editProduct.category,
          color_accent: editProduct.color_accent,
          stock_quantity: editProduct.stock_quantity,
          max_order_qty: editProduct.max_order_qty,
          max_order_qty_enabled: editProduct.max_order_qty_enabled,
        };
        await adminProducts.update(editProduct.id, payload);
      }
      loadProducts();
      setEditProduct(null);
      setIsCreating(false);
      setPendingImageFile(null);
      setImagePreview(null);
    } catch (err: any) {
      console.error(err);
      setSaveError(err?.message || 'Failed to save product');
      setUploading(false);
    }
    finally { setSaving(false); }
  };

  const updateField = <K extends keyof ProductRow>(key: K, value: ProductRow[K]) => {
    if (!editProduct) return;
    setEditProduct({ ...editProduct, [key]: value });
  };

  /** Downscale an image file on the client so it's under 2 MB before upload. */
  const downscaleImage = (file: File, maxBytes = 2 * 1024 * 1024): Promise<File> => {
    return new Promise((resolve, reject) => {
      if (file.size <= maxBytes && /^image\/(jpeg|webp)$/i.test(file.type)) {
        resolve(file);
        return;
      }
      const img = new window.Image();
      const url = URL.createObjectURL(file);
      img.onload = () => {
        URL.revokeObjectURL(url);
        let { width, height } = img;
        const maxDim = 1200;
        if (Math.max(width, height) > maxDim) {
          const ratio = maxDim / Math.max(width, height);
          width = Math.round(width * ratio);
          height = Math.round(height * ratio);
        }
        const canvas = document.createElement('canvas');
        canvas.width = width;
        canvas.height = height;
        const ctx = canvas.getContext('2d')!;
        ctx.drawImage(img, 0, 0, width, height);

        const tryQuality = (q: number) => {
          canvas.toBlob(
            blob => {
              if (!blob) { reject(new Error('Failed to compress image')); return; }
              if (blob.size <= maxBytes || q <= 0.2) {
                resolve(new File([blob], file.name.replace(/\.[^.]+$/, '.webp'), { type: 'image/webp' }));
              } else {
                tryQuality(q - 0.1);
              }
            },
            'image/webp',
            q,
          );
        };
        tryQuality(0.9);
      };
      img.onerror = () => { URL.revokeObjectURL(url); reject(new Error('Failed to load image')); };
      img.src = url;
    });
  };

  const handleImageSelect = async (e: React.ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    if (!file) return;
    try {
      const compressed = await downscaleImage(file);
      setPendingImageFile(compressed);
      setImagePreview(URL.createObjectURL(compressed));
    } catch (err) {
      console.error('Image processing failed:', err);
      alert(err instanceof Error ? err.message : 'Image processing failed');
    } finally {
      e.target.value = '';
    }
  };

  const inputCls = `w-full px-3 py-2 rounded-lg border text-sm outline-none transition-colors ${
    isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500 focus:border-emerald-500' : 'bg-white border-gray-200 placeholder-gray-400 focus:border-emerald-500'
  }`;
  const labelCls = `block text-xs font-medium mb-1 ${isDark ? 'text-gray-400' : 'text-gray-500'}`;
  const sectionCls = `p-5 rounded-xl border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`;

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin w-8 h-8 border-4 border-emerald-500 border-t-transparent rounded-full" />
      </div>
    );
  }

  const inStock = products.filter(p => p.in_stock).length;
  const outStock = products.length - inStock;

  return (
    <div className="space-y-6">
      <div className="flex items-center justify-between flex-wrap gap-4">
        <div>
          <h1 className="text-2xl font-bold flex items-center gap-2"><Package size={24} /> Inventory</h1>
          <p className={`text-sm ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
            {products.length} products &middot; {inStock} in stock &middot; {outStock} out of stock
          </p>
        </div>
        <button
          onClick={openCreate}
          className="flex items-center gap-1.5 px-4 py-2 bg-emerald-500 hover:bg-emerald-600 text-white text-sm font-medium rounded-lg transition-colors"
        >
          <Plus size={16} /> Add Product
        </button>
      </div>

      {/* Tabs */}
      <div className="flex items-center gap-1">
        <button
          onClick={() => setActiveTab('products')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'products'
              ? 'bg-emerald-500 text-white'
              : isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          <Package size={16} /> Products
        </button>
        <button
          onClick={() => setActiveTab('notifications')}
          className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
            activeTab === 'notifications'
              ? 'bg-emerald-500 text-white'
              : isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'
          }`}
        >
          <Bell size={16} /> Notify Requests
          {notifications.length > 0 && (
            <span className={`ml-1 px-1.5 py-0.5 rounded-full text-[10px] font-bold ${
              activeTab === 'notifications' ? 'bg-white/20 text-white' : 'bg-emerald-500/10 text-emerald-500'
            }`}>
              {notifications.length}
            </span>
          )}
        </button>
      </div>

      {activeTab === 'products' && (<>
      {/* Filters */}
      <div className={`flex flex-wrap items-center gap-3 p-4 rounded-xl border ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <div className="relative flex-1 min-w-[200px]">
          <Search size={16} className={`absolute left-3 top-1/2 -translate-y-1/2 ${isDark ? 'text-gray-500' : 'text-gray-400'}`} />
          <input type="text" placeholder="Search products..." value={search} onChange={e => setSearch(e.target.value)}
            className={`w-full pl-9 pr-4 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white placeholder-gray-500' : 'bg-gray-50 border-gray-200 placeholder-gray-400'}`} />
        </div>
        <select value={brandFilter} onChange={e => setBrandFilter(e.target.value)}
          className={`px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}>
          <option value="all">All Brands</option>
          {brands.map(b => <option key={b} value={b}>{b}</option>)}
        </select>
        <select value={stockFilter} onChange={e => setStockFilter(e.target.value as 'all' | 'in' | 'out')}
          className={`px-3 py-2 rounded-lg border text-sm outline-none ${isDark ? 'bg-gray-800 border-gray-700 text-white' : 'bg-gray-50 border-gray-200'}`}>
          <option value="all">All Stock</option>
          <option value="in">In Stock</option>
          <option value="out">Out of Stock</option>
        </select>
      </div>

      {/* Table */}
      <div className={`rounded-xl border overflow-x-auto ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
        <table className="w-full text-sm">
          <thead>
            <tr className={`border-b ${isDark ? 'border-gray-800 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
              <th className="text-left px-4 py-3 font-semibold">Image</th>
              <th className="text-left px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('name')}>
                <span className="flex items-center gap-1">Product {sortIcon('name')}</span>
              </th>
              <th className="text-left px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('brand')}>
                <span className="flex items-center gap-1">Brand {sortIcon('brand')}</span>
              </th>
              <th className="text-left px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('strength')}>
                <span className="flex items-center gap-1">mg {sortIcon('strength')}</span>
              </th>
              <th className="text-left px-4 py-3 font-semibold cursor-pointer select-none" onClick={() => handleSort('price')}>
                <span className="flex items-center gap-1">Price {sortIcon('price')}</span>
              </th>
              <th className="text-center px-4 py-3 font-semibold">Stock</th>
              <th className="text-center px-4 py-3 font-semibold">Qty</th>
              <th className="text-center px-4 py-3 font-semibold">Flags</th>
              <th className="text-center px-4 py-3 font-semibold">Actions</th>
            </tr>
          </thead>
          <tbody>
            {filtered.map(p => (
              <tr key={p.id} className={`border-b transition-colors ${isDark ? 'border-gray-800 hover:bg-gray-800/30' : 'border-gray-100 hover:bg-gray-50'}`}>
                <td className="px-4 py-2">
                  {p.image ? (
                    <img src={p.image} alt={`${p.name} thumbnail`} className="w-10 h-10 object-contain rounded-lg" />
                  ) : (
                    <div className={`w-10 h-10 rounded-lg flex items-center justify-center ${isDark ? 'bg-gray-800' : 'bg-gray-100'}`}>
                      <Image size={14} className="text-gray-400" />
                    </div>
                  )}
                </td>
                <td className="px-4 py-3">
                  <p className="font-medium">{p.name}</p>
                  <p className={`text-xs ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{p.category} &middot; {p.flavor}</p>
                </td>
                <td className="px-4 py-3">
                  <span className={`px-2 py-0.5 rounded-full text-xs font-medium ${isDark ? 'bg-gray-800 text-gray-300' : 'bg-gray-100 text-gray-600'}`}>
                    {p.brand}
                  </span>
                </td>
                <td className="px-4 py-3">{p.strength}mg</td>
                <td className="px-4 py-3">
                  <div>
                    <span className="font-semibold">₹{p.price.toLocaleString('en-IN')}</span>
                    {p.original_price && (
                      <span className={`ml-1 text-xs line-through ${isDark ? 'text-gray-600' : 'text-gray-400'}`}>
                        ₹{p.original_price.toLocaleString('en-IN')}
                      </span>
                    )}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <button onClick={() => toggleStock(p)} title={p.in_stock ? 'Mark out of stock' : 'Mark in stock'}>
                    {p.in_stock ? <ToggleRight size={24} className="text-emerald-500" /> : <ToggleLeft size={24} className="text-gray-400" />}
                  </button>
                </td>
                <td className={`px-4 py-3 text-center font-medium ${
                  p.stock_quantity === 0 ? 'text-red-500' : p.stock_quantity <= 5 ? 'text-amber-500' : (isDark ? 'text-gray-300' : 'text-gray-700')
                }`}>
                  {p.stock_quantity}
                </td>
                <td className="px-4 py-2 text-center">
                  <div className="flex items-center justify-center gap-1 flex-wrap">
                    {p.best_seller && <span className="px-1.5 py-0.5 bg-amber-500/10 text-amber-500 text-[10px] font-bold rounded">BS</span>}
                    {p.featured && <span className="px-1.5 py-0.5 bg-blue-500/10 text-blue-500 text-[10px] font-bold rounded">FT</span>}
                    {p.is_new && <span className="px-1.5 py-0.5 bg-emerald-500/10 text-emerald-500 text-[10px] font-bold rounded">NEW</span>}
                  </div>
                </td>
                <td className="px-4 py-3 text-center">
                  <div className="flex items-center justify-center gap-1">
                    <button onClick={() => openEdit(p)} className="p-1.5 rounded-lg text-blue-400 hover:bg-blue-500/10 transition-colors" title="Edit product">
                      <Edit3 size={15} />
                    </button>
                    <button onClick={() => deleteProduct(p)} className="p-1.5 rounded-lg text-red-400 hover:bg-red-500/10 transition-colors" title="Delete product">
                      <Trash2 size={15} />
                    </button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
        {filtered.length === 0 && (
          <div className="p-8 text-center">
            <Filter size={32} className={`mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
            <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No products match your filters</p>
          </div>
        )}
      </div>
      </>)}

      {activeTab === 'notifications' && (
        <div className={`rounded-xl border overflow-x-auto ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
          {notifLoading ? (
            <div className="flex items-center justify-center h-40">
              <div className="animate-spin w-6 h-6 border-4 border-emerald-500 border-t-transparent rounded-full" />
            </div>
          ) : notifications.length === 0 ? (
            <div className="p-8 text-center">
              <Bell size={32} className={`mx-auto mb-2 ${isDark ? 'text-gray-600' : 'text-gray-300'}`} />
              <p className={`text-sm ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>No notification requests yet</p>
            </div>
          ) : (
            <table className="w-full text-sm">
              <thead>
                <tr className={`border-b ${isDark ? 'border-gray-800 bg-gray-800/50' : 'border-gray-100 bg-gray-50'}`}>
                  <th className="text-left px-4 py-3 font-semibold">Product</th>
                  <th className="text-left px-4 py-3 font-semibold">Email</th>
                  <th className="text-left px-4 py-3 font-semibold">Mobile</th>
                  <th className="text-left px-4 py-3 font-semibold">User</th>
                  <th className="text-center px-4 py-3 font-semibold">Status</th>
                  <th className="text-left px-4 py-3 font-semibold">Date</th>
                </tr>
              </thead>
              <tbody>
                {notifications.map(n => (
                  <tr key={n.id} className={`border-b transition-colors ${isDark ? 'border-gray-800 hover:bg-gray-800/30' : 'border-gray-100 hover:bg-gray-50'}`}>
                    <td className="px-4 py-3 font-medium">{n.product_name ?? n.product_id}</td>
                    <td className="px-4 py-3">{n.email}</td>
                    <td className="px-4 py-3">{n.user_phone ?? <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>—</span>}</td>
                    <td className="px-4 py-3">{n.user_name ?? <span className={isDark ? 'text-gray-500' : 'text-gray-400'}>Guest</span>}</td>
                    <td className="px-4 py-3 text-center">
                      {n.notified ? (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-emerald-500/10 text-emerald-500">Notified</span>
                      ) : (
                        <span className="px-2 py-0.5 rounded-full text-xs font-medium bg-amber-500/10 text-amber-500">Pending</span>
                      )}
                    </td>
                    <td className={`px-4 py-3 text-xs ${isDark ? 'text-gray-400' : 'text-gray-500'}`}>
                      {new Date(n.created_at).toLocaleDateString('en-IN', { day: '2-digit', month: 'short', year: 'numeric' })}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          )}
        </div>
      )}

      {/* ──── EDIT / CREATE MODAL ──── */}
      {editProduct && (
        <div className="fixed inset-0 z-[60] flex items-start justify-center overflow-y-auto">
          <div className="fixed inset-0 bg-black/60 backdrop-blur-sm" onClick={() => { if (!saving) { setEditProduct(null); setPendingImageFile(null); setImagePreview(null); } }} />
          <div className={`relative my-8 w-full max-w-3xl mx-4 rounded-2xl shadow-2xl border overflow-hidden ${isDark ? 'bg-gray-900 border-gray-800' : 'bg-white border-gray-200'}`}>
            {/* Header */}
            <div className={`flex items-center justify-between px-6 py-4 border-b ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              <h2 className="text-lg font-bold flex items-center gap-2">
                {isCreating ? <><Plus size={18} className="text-emerald-500" /> New Product</> : <><Edit3 size={18} className="text-blue-500" /> Edit Product</>}
              </h2>
              <button onClick={() => { if (!saving) { setEditProduct(null); setPendingImageFile(null); setImagePreview(null); } }} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-gray-800' : 'hover:bg-gray-200'}`}>
                <X size={18} />
              </button>
            </div>

            {/* Body */}
            <div className="p-6 space-y-6 max-h-[70vh] overflow-y-auto">
              {/* Basic info */}
              <div className={sectionCls}>
                <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Tag size={14} /> Basic Information
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
                  <div className="md:col-span-2">
                    <label className={labelCls}>Product Name *</label>
                    <input type="text" value={editProduct.name} onChange={e => updateField('name', e.target.value)} className={inputCls} placeholder="VELO Cool Mint 10mg" />
                  </div>
                  <div>
                    <label className={labelCls}>Brand *</label>
                    {addingNewBrand ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newBrandName}
                          onChange={e => setNewBrandName(e.target.value)}
                          className={inputCls}
                          placeholder="New brand name"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const name = newBrandName.trim();
                            if (name && !brands.includes(name)) {
                              setCustomBrands(prev => [...prev, name]);
                            }
                            if (name) updateField('brand', name);
                            setNewBrandName('');
                            setAddingNewBrand(false);
                          }}
                          className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => { setNewBrandName(''); setAddingNewBrand(false); }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <select value={editProduct.brand} onChange={e => updateField('brand', e.target.value)} className={`${inputCls} flex-1`}>
                          {brands.map(b => <option key={b} value={b}>{b}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => setAddingNewBrand(true)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                          title="Add new brand"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Flavor *</label>
                    {addingNewCategory ? (
                      <div className="flex gap-2">
                        <input
                          type="text"
                          value={newCategoryName}
                          onChange={e => setNewCategoryName(e.target.value)}
                          className={inputCls}
                          placeholder="New flavor name"
                          autoFocus
                        />
                        <button
                          type="button"
                          onClick={() => {
                            const name = newCategoryName.trim();
                            if (name && !categories.includes(name)) {
                              setCustomCategories(prev => [...prev, name]);
                            }
                            if (name) updateField('category', name);
                            setNewCategoryName('');
                            setAddingNewCategory(false);
                          }}
                          className="px-3 py-2 rounded-lg bg-emerald-600 text-white text-sm font-medium hover:bg-emerald-700 transition-colors whitespace-nowrap"
                        >
                          Add
                        </button>
                        <button
                          type="button"
                          onClick={() => { setNewCategoryName(''); setAddingNewCategory(false); }}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                        >
                          Cancel
                        </button>
                      </div>
                    ) : (
                      <div className="flex gap-2">
                        <select value={editProduct.category} onChange={e => updateField('category', e.target.value)} className={`${inputCls} flex-1`}>
                          {categories.map(c => <option key={c} value={c}>{c}</option>)}
                        </select>
                        <button
                          type="button"
                          onClick={() => setAddingNewCategory(true)}
                          className={`px-3 py-2 rounded-lg text-sm font-medium transition-colors whitespace-nowrap ${isDark ? 'bg-gray-700 text-gray-300 hover:bg-gray-600' : 'bg-gray-200 text-gray-600 hover:bg-gray-300'}`}
                          title="Add new flavor"
                        >
                          <Plus size={16} />
                        </button>
                      </div>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Strength (mg) *</label>
                    <input type="number" value={editProduct.strength} onChange={e => updateField('strength', parseFloat(e.target.value) || 0)} className={inputCls} step="0.5" min="0" />
                  </div>
                  <div>
                    <label className={labelCls}>Pouches per Can</label>
                    <input type="number" value={editProduct.pouches_per_can} onChange={e => updateField('pouches_per_can', parseInt(e.target.value) || 20)} className={inputCls} min="1" />
                  </div>
                </div>
              </div>

              {/* Pricing */}
              <div className={sectionCls}>
                <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <DollarSign size={14} /> Pricing & Discount
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Price (₹) *</label>
                    <input type="number" value={editProduct.price} onChange={e => updateField('price', parseInt(e.target.value) || 0)} className={inputCls} min="0" />
                  </div>
                  <div>
                    <label className={labelCls}>Original Price (₹)</label>
                    <input type="number" value={editProduct.original_price ?? ''} onChange={e => updateField('original_price', e.target.value ? parseInt(e.target.value) : null)} className={inputCls} placeholder="Leave empty = no discount" />
                  </div>
                  <div>
                    <label className={labelCls}>Discount</label>
                    <div className={`px-3 py-2 rounded-lg text-sm ${isDark ? 'bg-gray-800 text-gray-400' : 'bg-gray-50 text-gray-500'}`}>
                      {editProduct.original_price && editProduct.original_price > editProduct.price
                        ? `${Math.round((1 - editProduct.price / editProduct.original_price) * 100)}% OFF`
                        : 'No discount'}
                    </div>
                  </div>
                </div>
              </div>

              {/* Image & description */}
              <div className={sectionCls}>
                <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <FileText size={14} /> Content
                </h3>
                <div className="space-y-4">
                  <div>
                    <label className={labelCls}>Product Image</label>
                    <div className="flex items-center gap-4">
                      <label
                        className={`flex items-center gap-2 px-4 py-2 rounded-lg border text-sm font-medium cursor-pointer transition-colors ${
                          uploading ? 'opacity-50 pointer-events-none' : ''
                        } ${isDark ? 'bg-gray-800 border-gray-700 text-gray-300 hover:border-emerald-500' : 'bg-gray-50 border-gray-200 text-gray-600 hover:border-emerald-500'}`}
                      >
                        {uploading ? (
                          <><Loader2 size={16} className="animate-spin" /> Uploading...</>
                        ) : (
                          <><Upload size={16} /> {(pendingImageFile || editProduct.image) ? 'Change Image' : 'Select Image'}</>
                        )}
                        <input
                          type="file"
                          accept="image/jpeg,image/png,image/webp,image/avif"
                          onChange={handleImageSelect}
                          className="hidden"
                          disabled={uploading}
                        />
                      </label>
                      {(imagePreview || editProduct.image) && (
                        <div className="flex items-center gap-2">
                          <img
                            src={imagePreview || editProduct.image!}
                            alt={`Image preview for ${editProduct.name || 'product'}`}
                            className={`w-12 h-12 object-contain rounded-lg border ${isDark ? 'border-gray-700' : 'border-gray-200'}`}
                          />
                          <button
                            type="button"
                            onClick={() => { updateField('image', null); setImagePreview(null); setPendingImageFile(null); }}
                            className="p-1 rounded text-red-400 hover:bg-red-500/10 transition-colors"
                            title="Remove image"
                          >
                            <X size={14} />
                          </button>
                        </div>
                      )}
                    </div>
                    {editProduct.image && (
                      <p className={`text-xs mt-1 truncate ${isDark ? 'text-gray-500' : 'text-gray-400'}`}>{editProduct.image}</p>
                    )}
                  </div>
                  <div>
                    <label className={labelCls}>Short Description *</label>
                    <textarea value={editProduct.description} onChange={e => updateField('description', e.target.value)} className={`${inputCls} resize-none`} rows={2} placeholder="Brief product description..." />
                  </div>
                  <div>
                    <label className={labelCls}>Long Description</label>
                    <textarea value={editProduct.long_description ?? ''} onChange={e => updateField('long_description', e.target.value || null)} className={`${inputCls} resize-none`} rows={4} placeholder="Detailed product description for product page..." />
                  </div>
                </div>
              </div>

              {/* Inventory */}
              <div className={sectionCls}>
                <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Package size={14} /> Inventory
                </h3>
                <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
                  <div>
                    <label className={labelCls}>Stock Quantity</label>
                    <input type="number" value={editProduct.stock_quantity} onChange={e => updateField('stock_quantity', parseInt(e.target.value) || 0)} className={inputCls} min="0" />
                  </div>
                  <div>
                    <label className={labelCls}>Max Order Qty</label>
                    <input type="number" value={editProduct.max_order_qty} onChange={e => updateField('max_order_qty', parseInt(e.target.value) || 1)} className={`${inputCls} ${!editProduct.max_order_qty_enabled ? 'opacity-50 cursor-not-allowed' : ''}`} min="1" disabled={!editProduct.max_order_qty_enabled} />
                  </div>
                  <div className="flex items-end pb-1">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editProduct.max_order_qty_enabled}
                        onChange={e => updateField('max_order_qty_enabled', e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>Limit orders per customer</span>
                    </label>
                  </div>
                </div>
              </div>

              {/* Flags & Display */}
              <div className={sectionCls}>
                <h3 className={`text-sm font-semibold mb-4 flex items-center gap-2 ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>
                  <Image size={14} /> Display & Flags
                </h3>
                <div className="grid grid-cols-2 md:grid-cols-4 gap-4">
                  {([
                    ['in_stock', 'In Stock'],
                    ['featured', 'Featured'],
                    ['best_seller', 'Bestseller'],
                    ['is_new', 'New Arrival'],
                  ] as const).map(([key, label]) => (
                    <label key={key} className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={editProduct[key] as boolean}
                        onChange={e => updateField(key, e.target.checked)}
                        className="w-4 h-4 rounded border-gray-300 text-emerald-600 focus:ring-emerald-500"
                      />
                      <span className={`text-sm ${isDark ? 'text-gray-300' : 'text-gray-700'}`}>{label}</span>
                    </label>
                  ))}
                </div>
                <div className="mt-4">
                  <label className={labelCls}>Color Accent</label>
                  <div className="flex items-center gap-3">
                    <input type="color" value={editProduct.color_accent} onChange={e => updateField('color_accent', e.target.value)} className="w-10 h-10 rounded-lg cursor-pointer border-0" />
                    <input type="text" value={editProduct.color_accent} onChange={e => updateField('color_accent', e.target.value)} className={`w-32 ${inputCls}`} placeholder="#10b981" />
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className={`px-6 py-4 border-t ${isDark ? 'border-gray-800' : 'border-gray-100'}`}>
              {saveError && (
                <div className="mb-3 px-3 py-2 rounded-lg bg-red-500/10 border border-red-500/20 text-red-500 text-sm">
                  {saveError}
                </div>
              )}
              <div className="flex items-center justify-between">
                <button onClick={() => { if (!saving) { setEditProduct(null); setPendingImageFile(null); setImagePreview(null); } }} className={`px-4 py-2 rounded-lg text-sm font-medium transition-colors ${isDark ? 'text-gray-400 hover:text-white hover:bg-gray-800' : 'text-gray-500 hover:text-gray-700 hover:bg-gray-100'}`}>
                  Cancel
                </button>
                <button
                  onClick={handleSave}
                  disabled={saving || !editProduct.name || !editProduct.description}
                  className="flex items-center gap-1.5 px-5 py-2 bg-emerald-500 hover:bg-emerald-600 disabled:opacity-50 text-white text-sm font-medium rounded-lg transition-colors"
                >
                  <Save size={16} /> {saving ? 'Saving...' : isCreating ? 'Create Product' : 'Save Changes'}
                </button>
              </div>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}
